import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withStyles } from '@mui/styles';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Typography,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Link } from 'react-router-dom';

import { withRouter } from 'utils';
import { DateUtil } from 'doctivity-shared/utils';
import { getMileageLog, deleteMileageLog } from 'store/actions/mileageActions';
import { showConfirmationDialog } from 'store/actions/systemActions';

import MileageDialog from './MileageDialog';

const styles = (theme) => ({
  content: {
    padding: theme.spacing(3),
  },
  card: {
    marginBottom: theme.spacing(3),
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  cardTitle: {
    flex: 1,
    fontWeight: 600,
    color: theme.palette.primary.dark,
  },
  editButton: {
    color: theme.palette.secondary.main,
  },
  leftIcon: {
    marginRight: theme.spacing(0.5),
  },
  row: {
    display: 'flex',
    marginBottom: theme.spacing(1.5),
  },
  label: {
    width: 180,
    color: theme.palette.text.secondary,
    fontSize: 13,
    fontWeight: 500,
    flexShrink: 0,
  },
  value: {
    fontSize: 14,
  },
  loadingBox: {
    padding: theme.spacing(6),
    display: 'flex',
    justifyContent: 'center',
  },
});

class MileageLogDetailPage extends Component {
  constructor(props) {
    super(props);
    this.state = { editDialogOpen: false };
  }

  componentDidMount() {
    this._loadLog();
  }

  componentDidUpdate(prevProps) {
    const { router, app } = this.props;
    const id = router.params.id;
    if (
      id !== prevProps.router.params.id ||
      app.selectedClient !== prevProps.app.selectedClient
    ) {
      this._loadLog();
    }
  }

  _loadLog() {
    const { dispatch, router } = this.props;
    const id = router.params.id;
    if (id) dispatch(getMileageLog(id));
  }

  onDelete = () => {
    const { dispatch, router, log } = this.props;
    dispatch(
      showConfirmationDialog({
        title: 'Delete Mileage Record',
        content: 'Delete this mileage record? This cannot be undone.',
        confirmLabel: 'Delete',
        onConfirm: () => {
          dispatch(deleteMileageLog(log.id));
          router.navigate('/notes/mileage');
        },
      })
    );
  };

  render() {
    const { classes, log, loading, isAdmin, currentUserId } = this.props;
    const { editDialogOpen } = this.state;

    if (loading) {
      return (
        <Box className={classes.loadingBox}>
          <CircularProgress />
        </Box>
      );
    }

    if (!log) {
      return (
        <Box sx={{ p: 4 }}>
          <Typography color='error'>Mileage record not found.</Typography>
          <Button component={Link} to='/notes/mileage' sx={{ mt: 2 }}>
            ← Back to Mileage Tracking
          </Button>
        </Box>
      );
    }

    const reimbursement =
      log.is_business
        ? `$${parseFloat(log.reimbursement || 0).toFixed(2)}`
        : '$0.00 (Personal)';

    const Field = ({ label, value }) => (
      <div className={classes.row}>
        <Typography className={classes.label}>{label}</Typography>
        <Typography className={classes.value}>{value || '—'}</Typography>
      </div>
    );

    return (
      <>
        <title>Mileage Record — {DateUtil.formatDateForUser(log.date_of_trip)}</title>

        <div className={classes.content}>
          <Card className={classes.card}>
            <CardContent>
              <div className={classes.cardHeader}>
                <Typography className={classes.cardTitle}>Mileage Record</Typography>
                {!isAdmin && log.user_id === currentUserId && (
                  <Button
                    variant='text'
                    className={classes.editButton}
                    onClick={() => this.setState({ editDialogOpen: true })}
                  >
                    <EditOutlinedIcon className={classes.leftIcon} />
                    Edit
                  </Button>
                )}
              </div>
              <Field label='Date of Trip'     value={DateUtil.formatDateForUser(log.date_of_trip)} />
              <Field label='Trip Start'       value={log.trip_start} />
              <Field label='Trip Destination' value={log.trip_destination} />
              <Field label='Round Trip'       value={log.is_round_trip ? 'Yes' : 'No'} />
              <Field
                label='Trip Type'
                value={log.is_business ? 'Business' : 'Personal'}
              />
              <Field label='Purpose' value={log.purpose} />
              <Divider sx={{ my: 1.5 }} />
              <Field label='Miles' value={log.mileage ? `${log.mileage} mi` : undefined} />
              {log.start_odometer != null && (
                <Field label='Start Odometer' value={log.start_odometer} />
              )}
              {log.end_odometer != null && (
                <Field label='End Odometer' value={log.end_odometer} />
              )}
              <Field label='Reimbursement' value={reimbursement} />
              {log.user && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Field
                    label='Logged By'
                    value={`${log.user.first_name} ${log.user.last_name}`}
                  />
                </>
              )}
              {log.activity_note && (
                <Field
                  label='Activity Note'
                  value={
                    <Button
                      component={Link}
                      to={`/notes/${log.activity_note.id}`}
                      size='small'
                      sx={{ p: 0, minWidth: 0 }}
                    >
                      {DateUtil.formatDateTimeFromDB(log.activity_note.comment_date)}
                    </Button>
                  }
                />
              )}
            </CardContent>
          </Card>

        </div>

        {editDialogOpen && (
          <MileageDialog
            open={editDialogOpen}
            log={log}
            clientId={this.props.app.selectedClient}
            onClose={() => this.setState({ editDialogOpen: false })}
            onSaved={() => {
              this.setState({ editDialogOpen: false });
              this._loadLog();
            }}
            onDelete={() => {
              this.setState({ editDialogOpen: false });
              this.onDelete();
            }}
          />
        )}
      </>
    );
  }
}

MileageLogDetailPage.propTypes = {
  classes:  PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  router:   PropTypes.object.isRequired,
  app:      PropTypes.object.isRequired,
  log:           PropTypes.object,
  loading:       PropTypes.bool,
  isAdmin:       PropTypes.bool,
  currentUserId: PropTypes.number,
};

function mapStateToProps(state) {
  return {
    app:           state.app,
    log:           state.mileage?.log?.data || null,
    loading:       state.mileage?.log?.loading || false,
    isAdmin:       state.user?.is_admin || false,
    currentUserId: state.user?.id || null,
  };
}

export default connect(mapStateToProps)(withRouter(withStyles(styles)(MileageLogDetailPage)));
